/**
 * The AI panel (Story 6), shared by detection detail and alert detail.
 *
 * Four states, one component:
 *   none     → if the role may triage, ask the API to enrich once (first
 *              open); otherwise say enrichment has not run.
 *   pending  → "Summarizing…" and re-fetch the parent query every 3 s,
 *              for at most a minute.
 *   ready    → the summary, goal, steps and remediation, labeled with the
 *              model id (Story 6: visibly AI-generated, alongside the
 *              evidence, never instead of it).
 *   failed   → the same "unavailable" sentence the email uses, the reason,
 *              and a Retry for roles that may triage.
 *
 * The request is fire-and-forget: the API claims the detection so two
 * analysts opening at once produce one Bedrock call, and the loser simply
 * sees `pending` and polls like everyone else.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError, requestEnrichment, type AiPanel as AiPanelData } from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { useMdrToken } from './context.js';
import { AI_PENDING, AI_UNAVAILABLE, timeAgo } from './format.js';
import { useBoundedPoll } from './useBoundedPoll.js';

const REASONS: Record<string, string> = {
  'cap_hit:tenant': "This Client's daily AI cap is spent; it resets at midnight UTC.",
  'cap_hit:platform': 'The platform-wide daily AI cap is spent; it resets at midnight UTC.',
  content_policy: 'The Bedrock Guardrail blocked the prompt or the response.',
  bad_output: 'The model returned something that was not the expected summary.',
};

function reasonText(reason: string | undefined): string {
  if (!reason) return '';
  if (REASONS[reason]) return REASONS[reason];
  if (reason.startsWith('bedrock_error'))
    return `Bedrock returned an error (${reason.split(':')[1] ?? 'unknown'}).`;
  return reason;
}

export function AiPanel({
  tenantId,
  detectionId,
  ai,
  canRequest,
  refreshKey,
  compact = false,
}: {
  tenantId: string;
  detectionId: string;
  ai: AiPanelData;
  /** Whether the signed-in role may ask for enrichment (not viewer). */
  canRequest: boolean;
  /** The query key of the screen that owns `ai`; re-fetched while pending. */
  refreshKey: unknown[];
  compact?: boolean;
}) {
  const idToken = useMdrToken();
  const queryClient = useQueryClient();
  const [requestError, setRequestError] = useState<unknown>(null);
  const [localPending, setLocalPending] = useState(false);
  const requested = useRef(false);

  const state = localPending && ai.state === 'none' ? 'pending' : ai.state;

  // First open: ask once, then let polling pick up the result.
  useEffect(() => {
    if (ai.state !== 'none' || !canRequest || requested.current || !idToken) return;
    requested.current = true;
    setLocalPending(true);
    requestEnrichment(tenantId, detectionId, idToken)
      .then(() => void queryClient.invalidateQueries({ queryKey: refreshKey }))
      .catch((error: unknown) => {
        setLocalPending(false);
        setRequestError(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ai.state, canRequest, idToken, tenantId, detectionId]);

  // Pending: poll the owning query, bounded.
  useBoundedPoll(state === 'pending', refreshKey);

  const retry = () => {
    requested.current = false;
    setRequestError(null);
    setLocalPending(true);
    requestEnrichment(tenantId, detectionId, idToken)
      .then(() => void queryClient.invalidateQueries({ queryKey: refreshKey }))
      .catch((error: unknown) => {
        setLocalPending(false);
        setRequestError(error);
      });
  };

  const textClass = compact ? 'text-sm leading-relaxed' : 'text-sm leading-relaxed';

  if (state === 'ready' && ai.summary) {
    return (
      <div className={`space-y-3 ${textClass}`}>
        <p>{ai.summary}</p>
        {ai.suspected_goal && (
          <p>
            <span className="font-semibold">Suspected goal: </span>
            {ai.suspected_goal}
          </p>
        )}
        {!compact && ai.investigation_steps && ai.investigation_steps.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-bluesphere-muted">
              Suggested investigation
            </div>
            <ol className="mt-1 list-decimal space-y-1 pl-5">
              {ai.investigation_steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        )}
        {!compact && ai.remediation && ai.remediation.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-bluesphere-muted">
              Remediation to consider
            </div>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {ai.remediation.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-[11px] text-bluesphere-muted">
          Generated by <span className="font-mono">{ai.model ?? 'Claude'}</span>
          {ai.quality_degraded ? ' (fallback model)' : ''}
          {ai.generated_at ? ` · ${timeAgo(ai.generated_at)}` : ''} · AI-generated, advisory, never
          decides. Review against the provider evidence before acting.
        </p>
      </div>
    );
  }

  if (state === 'pending') {
    return (
      <div className={`flex items-center gap-3 ${textClass} text-bluesphere-muted`} role="status">
        <span
          aria-hidden="true"
          className="inline-block h-4 w-4 animate-spin rounded-full border-b-2 border-bluesphere-primary"
        />
        {AI_PENDING}
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <div className={`space-y-2 ${textClass}`}>
        <p className="text-bluesphere-muted">{AI_UNAVAILABLE}</p>
        {ai.failed_reason && (
          <p className="text-xs text-bluesphere-textSecondary">{reasonText(ai.failed_reason)}</p>
        )}
        {canRequest && (
          <PillButton variant="outline" size="sm" onClick={retry}>
            Try again
          </PillButton>
        )}
        {requestError != null && <RequestError error={requestError} />}
      </div>
    );
  }

  // none
  return (
    <div className={`space-y-2 ${textClass}`}>
      <p className="text-bluesphere-muted">
        {AI_UNAVAILABLE} Enrichment has not run for this detection
        {canRequest ? '.' : '; a role that can triage can request it.'}
      </p>
      {requestError != null && <RequestError error={requestError} />}
    </div>
  );
}

function RequestError({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? `${error.code}: ${error.message}`
      : error instanceof Error
        ? error.message
        : String(error);
  return <p className="text-xs text-red-700">Could not request enrichment. {message}</p>;
}
